import { transformEvaluationData } from "../../../pages/app/evaluation-list/evaluationListColumns";
import type { ComponentType } from "react";
import {
  TDataVisualizationConfig,
  type TQueryHook,
  type TColumnProviderProps,
  TRowClickContext,
} from "@/core/types/generic-data-visualization";
import { buildQueryParams } from "@/utils/commonUtils";

/**
 * Configuration type for data visualization modals
 */
export type TDataVisualizationConfigType =
  "evaluation-list" | "analytics-drilldown" | "employee-list";

/**
 * Registry of data visualization configurations
 * Add new configurations here for different data types
 */
export const dataVisualizationConfigRegistry: Record<
  string,
  TDataVisualizationConfig<any, any, any>
> = {
  "evaluation-list": {
    title: "Evaluation List",
    size: "8xl",
    // Type assertion needed because React Query's UseQueryResult doesn't have index signature
    // ✅ Lazy load the query hook
    loadQueryHook: () =>
      import("@/api/evaluation-listing").then(
        (m) => m.useGetEvaluationListQuery,
      ),
    // ✅ Lazy load the column provider
    loadColumnProvider: () =>
      import("@/pages/app/evaluation-list/EvaluationListColumnProvider").then(
        (m) => m.EvaluationListColumnProvider,
      ),
    transformData: transformEvaluationData,
    // ✅ Factory function that will be called in the component
    createRowClickHandler: (
      context: TRowClickContext<Record<string, string | undefined>>,
      evaluationId: string,
    ) => {
      context.setLoadingId(evaluationId);
      const basePath = `/evaluation-details/${evaluationId}`;
      const query = context.modalParams
        ? buildQueryParams(context.modalParams)
        : "";
      const fiullPath = query ? `${basePath}${query}` : basePath;
      window.open(fiullPath, "_blank");
      queueMicrotask(() => {
        context.setLoadingId(null);
      });
    },

    initialColumnPinning: {
      right: ["actions"],
    },
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
    },
  },

  "employee-list": {
    title: "Employee List",
    size: "8xl",
    // Type assertion needed because React Query's UseQueryResult doesn't have index signature
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then((m) => {
        const useGetEmployeeListQuery = m.useGetEmployeeListQuery;
        // Adapter: adapt narrower options type into what the generic modal expects.
        const useGetEmployeeListQueryAdapter: TQueryHook<
          Record<string, any>,
          any,
          any
        > = (params, options) => {
          const enabled =
            typeof options?.enabled === "boolean" ? options.enabled : undefined;

          const adaptedParams = { ...params } as Record<string, any>;
          // Generic modal uses `sort_by`, employee list API expects `sort_field`.
          const sortBy = adaptedParams.sort_by as unknown;
          if (typeof sortBy === "string" && sortBy.trim().length > 0) {
            adaptedParams.sort_field = sortBy;
            delete adaptedParams.sort_by;
          }

          return useGetEmployeeListQuery(adaptedParams, {
            enabled,
            refetchOnMount: options?.refetchOnMount as
              boolean | "always" | undefined,
          });
        };

        return useGetEmployeeListQueryAdapter;
      }),
    loadColumnProvider: () =>
      import("@/pages/app/employee-management/EmployeeListColumnProvider").then(
        (m: unknown) =>
          (
            m as {
              EmployeeListColumnProvider: ComponentType<TColumnProviderProps>;
            }
          ).EmployeeListColumnProvider,
      ),
    transformData: (
      apiData: unknown[],
    ): {
      id: string;
      employee_id: string;
      full_name: string;
      email: string;
      status: string;
      has_user_account: boolean;
    }[] => {
      return (apiData ?? []).map((item) => {
        const typedItem = item as {
          internal_id: string;
          employee_id?: string;
          first_name?: string;
          last_name?: string;
          email?: string;
          status?: string;
          has_user_account?: boolean;
        };

        const fullName =
          `${typedItem.first_name ?? ""} ${typedItem.last_name ?? ""}`
            .replace(/\s+/g, " ")
            .trim();

        return {
          id: typedItem.internal_id,
          employee_id: typedItem.employee_id ?? "",
          full_name: fullName || "-",
          email: typedItem.email ?? "-",
          status: typedItem.status ?? "-",
          has_user_account: Boolean(typedItem.has_user_account),
        };
      });
    },
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
    },
  },
};

/**
 * Hook to get configuration for a data visualization type
 */
export const useDataVisualizationConfig = (configType: string) => {
  if (!configType || !dataVisualizationConfigRegistry[configType]) {
    return null;
  }

  return dataVisualizationConfigRegistry[configType];
};
